import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  HStack,
  Heading,
  Icon,
  Image,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  VisuallyHidden,
  useColorModeValue,
} from '@chakra-ui/react';
import { EmailIcon } from '@chakra-ui/icons';
import { FaFacebook, FaInstagram, FaTwitter } from 'react-icons/fa';
import { LOGO_URL_LIGHT, STATE_TITLE_MAP } from '#constants';
import { BrandHeading } from './BrandHeading';
import { BsHouseFill } from 'react-icons/bs';
import { Link } from 'react-router-dom';
import { capitalize, checkState } from '#utils';
import { useEffect, useMemo } from 'react';
import { useUIStore } from '#store';

const POPULAR_STATES = [
  'california', 'colorado', 'utah', 'arizona', 'washington', 'oregon',
  'montana', 'wyoming', 'texas', 'florida', 'north-carolina', 'tennessee'
];

const ListHeader = ({ children }) => {
  return (
    <Stack>
      <Flex fontWeight={'500'} fontSize={'lg'} mb={2}>
        {children}
      </Flex>
      <Divider borderColor={'brand.100'} w={32} />
    </Stack>
  );
};

const SocialButton = ({ children, label, to }) => {
  return (
    <Link to={to} rel="noopener noreferrer" target="_blank">
      <Button
        bg={useColorModeValue('blackAlpha.100', 'whiteAlpha.100')}
        rounded={'full'}
        w={8}
        h={8}
        cursor={'pointer'}
        display={'inline-flex'}
        alignItems={'center'}
        justifyContent={'center'}
        transition={'background 0.3s ease'}
        _hover={{
          bg: useColorModeValue('blackAlpha.200', 'whiteAlpha.200'),
        }}
      >
        <VisuallyHidden>{label}</VisuallyHidden>
        {children}
      </Button>
    </Link>
  );
};

function StateLink({ state }) {
  return (
    <Flex>
      <Link to={`/state/${state}`}>
        <Text fontSize="sm" color="gray.600" noOfLines={1} _hover={{ color: 'green.600' }}>
          {STATE_TITLE_MAP[state]}
        </Text>
      </Link>
    </Flex>
  );
}

function OutingLink({ outing }) {
  return (
    <Flex>
      <Link to={outing.permalink}>
        <Text fontSize="sm" color="gray.600" noOfLines={1} _hover={{ color: 'green.600' }}>
          {capitalize(outing.name)}
        </Text>
      </Link>
    </Flex>
  );
}

function TopFooter() {
  const { getStateRecs, location, stateRecs, getCurrentLocation } = useUIStore(
    (state) => ({
      getStateRecs: state.getStateRecs,
      location: state.location,
      stateRecs: state.stateRecs,
      getCurrentLocation: state.getCurrentLocation,
    })
  );
  const stateCode = useMemo(
    () => checkState(location?.state),
    [location?.state]
  );

  useEffect(() => {
    getCurrentLocation();
  }, []);

  useEffect(() => {
    getStateRecs({ stateCode });
  }, [stateCode]);

  return (
    <Box 
      bg="linear-gradient(180deg, #f0f7f4 0%, #e8f5e9 100%)"
      py={10} 
      px={4}
      borderTop="4px solid"
      borderColor="green.400"
    >
      <Container maxW={'7xl'}>
        <Flex direction="column" alignItems={'center'} gap={10}>
          {/* Popular campgrounds section */}
          {stateRecs?.length > 0 && (
            <Flex direction="column" alignItems={['flex-start', 'center']}>
              <Heading display={['block', 'none']} fontSize="xl" mb={4}>
            Popular campgrounds in {stateCode}
          </Heading>
              <BrandHeading display={['none', 'block']} fontSize={['2xl', '3xl']} mb={4}>
                Popular campgrounds in {stateCode}
              </BrandHeading>
              <SimpleGrid
                columns={[1, 2, 3, 4]}
                alignSelf={['flex-start', 'center']}
                spacingX={10}
                spacingY={2}
              >
                {stateRecs.slice(0, 8).map((outing) => (
                  <OutingLink outing={outing} key={outing._id} />
                ))}
              </SimpleGrid>
            </Flex>
          )}

          {/* Browse by state section - hidden on mobile */}
          <Flex direction="column" alignItems={'center'} display={['none', 'flex']}>
            <BrandHeading fontSize={['xl', '2xl', '3xl']} mb={4}>
              Find a campground or permit in:
            </BrandHeading>
          <SimpleGrid
              columns={[3, 4, 6, 6]}
            alignSelf="center"
            spacingX={10}
            spacingY={2}
          >
              {POPULAR_STATES.map((state) => (
                <StateLink state={state} key={state} />
            ))}
          </SimpleGrid>
          </Flex>
        </Flex>
      </Container>
    </Box>
  );
}

function BottomFooter() {
  const currentYear = new Date().getFullYear();
  return (
    <Box bg={'#1c2e20'} color={'gray.100'}>
      <Container as={Stack} maxW={'7xl'} py={[6, 10]} px={[4, 6]}>
        <Flex
          justifyContent={'space-between'}
          w="100%"
          flexDirection={['column', 'row']}
          gap={[6, 0]}
        >
          {/* Logo - hidden on mobile */}
          <VStack 
            justifyContent={'center'} 
            alignItems={'flex-start'}
            display={['none', 'none', 'flex']}
          >
            <Image
              src={LOGO_URL_LIGHT}
              alt="Reserve Nature"
              boxSize={'70px'}
              mr={3}
              mb={2}
            />
            <Heading
              size={'lg'}
              cursor="pointer"
              display={'flex'}
              alignItems="center"
              fontFamily="sans-serif"
            >
              Reserve Nature
            </Heading>
          </VStack>
          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={[4, 8]}>
            <Stack align={'flex-start'} spacing={[2, 3]}>
              <ListHeader>
                <Text fontSize={['sm', 'md']}>COMPANY</Text>
              </ListHeader>
              <Link to={'/about-us'}>About Us</Link>
              <Link to={'/blogs'}>Blogs</Link>
              <Link to={'/how-it-works'}>How It Works</Link>
              <Link to={'/pricing'}>Pricing</Link>
              <Link to={'/payment-verification'}>Payment verification</Link>
              <Link to={'/faq'}>FAQs</Link>
            </Stack>

            <Stack align={'flex-start'} spacing={[2, 3]}>
              <ListHeader>
                <Text fontSize={['sm', 'md']}>LEGAL</Text>
              </ListHeader>
              <Link to={'/cookies-policy'}>Cookies Policy</Link>
              <Link to={'/privacy-policy'}>Privacy Policy</Link>
              <Link to={'/terms-of-service'}>Terms of Service</Link>
              <Link to={'/refunds'}>Refund Policy</Link>
            </Stack>
            <Stack spacing={[2, 4]} gridColumn={['span 2', 'span 1']}>
              <ListHeader>
                <Text fontSize={['sm', 'md']}>CONTACT</Text>
              </ListHeader>
              <HStack alignItems="center" spacing={3}>
                <Icon as={BsHouseFill} boxSize={[5, 6]} />
                <Text fontWeight={'medium'} fontSize={['sm', 'md']}>New York, NY, 11109, US</Text>
              </HStack>
              <HStack alignItems="center" spacing={3}>
                <Icon as={EmailIcon} boxSize={[5, 6]} />
                <Text fontWeight={'medium'} fontSize={['sm', 'md']}>contact@reservenature.com</Text>
              </HStack>
            </Stack>
          </SimpleGrid>
        </Flex>
      </Container>

      <Box
        borderTopWidth={1}
        borderStyle={'solid'}
        borderColor={useColorModeValue('gray.200', 'gray.700')}
      >
        <Container
          as={Stack}
          maxW={'6xl'}
          py={4}
          direction={{ base: 'column', md: 'row' }}
          spacing={4}
          justify={{ md: 'space-between' }}
          align={{ md: 'center' }}
        >
          <Text>© {currentYear} Reserve Nature. All rights reserved</Text>
          <Stack direction={'row'} spacing={6}>
            <SocialButton
              label={'Twitter'}
              to={'https://twitter.com/reservenature_'}
            >
              <Icon as={FaTwitter} />
            </SocialButton>
            <SocialButton
              label={'Instagram'}
              to={'https://www.instagram.com/reservenatureofficial/'}
            >
              <Icon as={FaInstagram} />
            </SocialButton>
            <SocialButton
              label={'Facebook'}
              to={'https://www.facebook.com/reservenature.ofc/'}
            >
              <Icon as={FaFacebook} />
            </SocialButton>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

export default function Footer() {
  return (
    <Flex direction={'column'}>
      <TopFooter />
      <BottomFooter />
    </Flex>
  );
}
